import * as React from 'react';
import { t } from '@reos/i18n';
import { V4Progress, V4Section, V4State, V4Why } from './ux-v4.js';
import type { CaseProgressView } from '../lib/case-progress.js';
import type { CaseJourney } from '../lib/case-journey.js';
import type { CockpitIssue, CockpitStatus } from '../lib/case-cockpit.js';
import type { CaseReviewAreas, ReviewArea } from '../lib/case-review-areas.js';

/**
 * Krok UXV4a2 — DETAIL ZAKAZKY JAKO JEDNA STRANKA (predloha
 * `docs/design/ux-v4/png/Zakazka.png` + `project/Zakazka.dc.html`).
 *
 * Poradi predlohy: navigacni lista → hero fotka s vyrezem → velky nadpis →
 * karta postupu → karta „Další krok" → sekce s rozbalenim (Prověření,
 * Nemovitost, Lidé, Dokumenty a smlouvy, Peníze, Historie).
 *
 * Co tady ZAMERNE neni: nacitani dat, router ani domenove vypocty. Stranka
 * (`routes/zakazka-detail.tsx`) dodava hotove uzly sekci — tahle komponenta
 * drzi STRUKTURU a stav rozbaleni. Duvod: obsah sekci jsou karty, ktere uz
 * existuji od kroku UXR3 (`case-aside.tsx`, `case-overview.tsx`) a nemelo by
 * smysl je prepisovat jen proto, ze se presunuly ze zalozky do rozbaleni.
 *
 * Komponenty jsou ze sdilene sady v4 (`components/ux-v4.tsx`, krok UXV4a) —
 * tenhle krok do ni pridal `V4NavBar`, `V4Why` a druhy radek hlavicky sekce.
 */

const cs = { locale: 'cs' } as const;

/** Kolik problemu stoji nahore nez se schovaji pod „N dalších" (predloha). */
export const VISIBLE_ISSUES = 2;

export interface CaseV4Section {
  /** Stabilni klic — `data-testid` hlavicky i stav rozbaleni. */
  readonly id: string;
  /** Ikona ve ctverci (uz obalena v `rh-v4-ico`). */
  readonly icon: React.ReactNode;
  readonly label: string;
  /** Druhy radek hlavicky — souhrn sekce. */
  readonly sub: React.ReactNode;
  /** Treti radek hlavicky — vlastni prouzek (dnes jen „Prověření"). */
  readonly extra?: React.ReactNode | undefined;
  readonly defaultOpen?: boolean | undefined;
  /**
   * `true` = sekce stoji jako VLASTNI skupina (predloha ma takto „Prověření",
   * oddelene mezerou); ostatni sekce jsou radky jedne spolecne skupiny.
   */
  readonly standalone?: boolean | undefined;
  readonly body: React.ReactNode;
}

/**
 * Krok UXF4 bod 3 (nalez #43 auditu GPT 9. 10. 2026) — PODKLADY JSOU DRUHY,
 * ODDELENY UKAZATEL. Do UXF4 stalo v karte jen „Hotovo 80 %" a vedle nej
 * v sekci „Prověření" veta „0 z 5 ověřeno"; cisla si zdanlive odporovala,
 * protoze jedno meri FAZE OBCHODU a druhe KVALITU PODKLADU. Ted stoji vedle
 * sebe, kazde se svym nazvem a vetou, co znamena. `undefined` = provereni
 * jeste nebezelo, takze o podkladech nevime nic a karta o nich mlci.
 */
export interface CaseMaterialsMeter {
  readonly done: number;
  readonly total: number;
}

export function CaseDetailV4({
  nav,
  title,
  subtitle,
  hero,
  progress,
  materials,
  journey,
  journeyActions,
  nextStep,
  sections,
  onOpenSection,
}: {
  readonly nav: React.ReactNode;
  readonly title: string;
  readonly subtitle: React.ReactNode;
  /** Hero fotka s vyrezem a tlacitkem — sklada ji stranka (`V4PhotoCard`). */
  readonly hero: React.ReactNode;
  readonly progress: CaseProgressView;
  readonly materials?: CaseMaterialsMeter | undefined;
  /**
   * Krok UXP4b bod 1 (nalez P2-5) — VSECH DESET FAZI JAKO ROZBALENI JEDINEHO
   * UKAZATELE. Do UXP4b stala na teze strance karta „Co zbývá? N z 10"
   * s vlastnim prouzkem a vlastnim procentem; makléř tak videl DVE ruzna
   * cisla o tomtez (pet fazi obchodu vs. deset stavu kanbanu). Cesta zakazky
   * nezmizela — je to PODROBNOST toho ukazatele, co stoji nad ni, a mapovani
   * desiti stavu na pet kroku zustava v `lib/case-phases.ts` (jeden zdroj).
   * `undefined` = casova osa se jeste nenacetla, rozbaleni se nevykresli.
   */
  readonly journey?: CaseJourney | undefined;
  /**
   * Krok NOC3 bod 1 (nalez N18) — AKCE U FAZE CESTY. Klic je `stage`
   * (`KANBAN_STAGE_ORDER`), hodnota hotovy uzel od stranky; komponenta sama
   * zadnou trasu nezna. Dnes tak faze „Inzerce" vede na rozpracovany inzerat
   * ve vsech stavech zakazky.
   */
  readonly journeyActions?: Readonly<Record<string, React.ReactNode>> | undefined;
  readonly nextStep: React.ReactNode;
  readonly sections: readonly CaseV4Section[];
  /**
   * Krok QAFIX11 bod 1 (nalez V5): otevre sekci detailu podle id z
   * `CaseProgressStep.section` — kroky pruhu postupu tak vedou tam, kam
   * ukazuji. Trasu zna STRANKA (`?sekce=`), komponenta ji nevymysli.
   */
  readonly onOpenSection?: ((section: string) => void) | undefined;
}): React.JSX.Element {
  const [open, setOpen] = React.useState<ReadonlySet<string>>(
    () => new Set(sections.filter((section) => section.defaultOpen === true).map((section) => section.id)),
  );
  /*
   * Krok PRF1 (vada V1, bod 4) — SEKCE, KTERA PRIBUDE AZ BEHEM ZIVOTA STRANKY.
   *
   * Pocatecni stav rozbaleni se cte z `defaultOpen` JEN pri pripojeni, takze
   * sekce, kterou do seznamu prida teprve akce makléře („Upravit" → sekce
   * „Zadání"), zustala ZAVRENA — na preview to vypadalo, ze „Upravit" nedela
   * nic (pruchod 13. 10., snimek 11l). Nove prichozi sekce s `defaultOpen`
   * se proto dorovna V RENDERU (efekt po pripojeni je zavod, viz
   * ROZHODNUTI UXR4-ZAVOD v `contracts.tsx`).
   *
   * Sekce, kterou uz makler rucne zavrel, se znovu neotevre — v `known`
   * zustava, dokud je v seznamu.
   */
  const [defaults, setDefaults] = React.useState<ReadonlyMap<string, boolean>>(
    () => new Map(sections.map((section) => [section.id, section.defaultOpen === true])),
  );
  /* Sekce, ktera PRIBYLA s `defaultOpen`, i sekce, u ktere se `defaultOpen`
     prave prepnul na `true` (tataz sekce „Zadání" prepnuta do editace). */
  const turnedOn = sections.filter(
    (section) => section.defaultOpen === true && defaults.get(section.id) !== true,
  );
  const defaultsChanged =
    sections.length !== defaults.size ||
    sections.some((section) => defaults.get(section.id) !== (section.defaultOpen === true));
  if (defaultsChanged) {
    setDefaults(new Map(sections.map((section) => [section.id, section.defaultOpen === true])));
    if (turnedOn.length > 0) setOpen(new Set([...open, ...turnedOn.map((section) => section.id)]));
  }
  const [journeyOpen, setJourneyOpen] = React.useState(false);
  const toggle = (id: string): void => {
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderSection = (section: CaseV4Section, bare: boolean): React.JSX.Element => (
    <V4Section
      key={section.id}
      bare={bare}
      icon={section.icon}
      label={section.label}
      sub={section.sub}
      {...(section.extra !== undefined ? { extra: section.extra } : {})}
      open={open.has(section.id)}
      onToggle={() => { toggle(section.id); }}
      testId={`case-section-toggle-${section.id}`}
    >
      <div className="rh-v4-sect__body" data-testid={`case-section-${section.id}`}>
        {section.body}
      </div>
    </V4Section>
  );

  return (
    <div className="rh-v4-page" data-testid="zakazka-detail">
      {nav}
      <div className="rh-v4-detail">
        <div className="rh-v4-detail__media">{hero}</div>
        <div className="rh-v4-detail__head">
          <h1 className="rh-v4-head__title" data-testid="case-title">
            {title}
          </h1>
          <div className="rh-v4-head__lede" data-testid="case-subtitle">
            {subtitle}
          </div>
        </div>
        {/*
          Krok UXP4b bod 1 (nalez P2-19): na 390 px stoji „Další krok" HNED pod
          nadpisem, nad kartou postupu — do UXP4b ho fotka, nadpis a postup
          odsunuly pod prehyb. Na desktopu se obe karty slozi do leveho
          sloupce ve stejnem poradi (grid-area `next` nad `main`), takze poradi
          pro ctecku i oko zustava jedno.
        */}
        <div className="rh-v4-detail__next">{nextStep}</div>
        <div className="rh-v4-detail__prog">
          {/*
            Karta postupu predlohy: „Hotovo X %", veta „co chybí", prouzek
            12 px a pod nim pet kroku. Cisla pocita JEDNA funkce
            (`lib/case-progress.ts`) — tatáz, jakou vola Dnesek i seznam.
          */}
          <section className="rh-v4-group rh-v4-card rh-v4-meters" data-testid="case-progress-card">
            {/*
              Krok UXF4 bod 3 — PRVNI UKAZATEL: postup zakazky fazemi. Nazev
              nad nim rika, co prouzek meri; „Postup zakázky" je tentyz klic
              katalogu, jaky nese pristupny nazev ukazatele (jedna veta, ne
              dve kopie).
            */}
            <div className="rh-v4-meter" data-testid="case-meter-progress">
              <span className="rh-v4-meter__name">{t('webAdmin.caseProgress.ariaLabel', cs)}</span>
              <span className="rh-v4-meter__explain">{t('webAdmin.caseDetailV4.progressExplain', cs)}</span>
              <V4Progress
                view={progress}
                size="lg"
                steps
                testId="case-progress"
                {...(onOpenSection === undefined ? {} : { onStepSelect: onOpenSection })}
              />
            </div>
            {/* DRUHY UKAZATEL: kvalita podkladu, nezavisla na fazi obchodu. */}
            {materials === undefined ? null : (
              <div className="rh-v4-meter" data-testid="case-meter-materials">
                <span className="rh-v4-meter__name" data-testid="case-materials-count">
                  {t('webAdmin.caseDetailV4.materialsHeading', {
                    ...cs,
                    values: { done: materials.done, total: materials.total },
                  })}
                </span>
                <span className="rh-v4-meter__explain">{t('webAdmin.caseDetailV4.materialsExplain', cs)}</span>
              </div>
            )}
            {journey === undefined ? null : (
              <CaseJourneyDetails
                journey={journey}
                {...(journeyActions === undefined ? {} : { actions: journeyActions })}
                open={journeyOpen}
                onToggle={() => { setJourneyOpen((value) => !value); }}
              />
            )}
          </section>
        </div>
        <div className="rh-v4-detail__main">
          <div className="rh-v4-groups" data-testid="case-sections">
            {/*
              „Prověření" je VLASTNI skupina (predloha ji ma oddelenou mezerou
              nad zbytkem); ostatni sekce stoji jako radky JEDNE skupiny pod
              sebou, oddelene odsazenou linkou — proto `bare`.
            */}
            {sections.filter((section) => section.standalone === true).map((section) => renderSection(section, false))}
            <section className="rh-v4-group">
              {sections.filter((section) => section.standalone !== true).map((section) => renderSection(section, true))}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

function czDate(iso: string): string {
  return new Date(iso).toLocaleDateString('cs-CZ', { dateStyle: 'medium' });
}

/**
 * Deset fazi zakazky jako ROZBALENI karty postupu (krok UXP4b bod 1).
 *
 * Proti zruseny karte „Co zbývá?" tu NENI ani prouzek, ani procento, ani
 * druhy pocet „N z 10" — to by byl presne ten druhy ukazatel, ktery nalez
 * P2-5 vytykal. Zustavaji jen faze s datem vstupu a stitek „Teď“, a to ze
 * TEHOZ vypoctu (`lib/case-journey.ts`) i tehoz katalogu jako driv.
 */
function CaseJourneyDetails({
  journey,
  actions,
  open,
  onToggle,
}: {
  readonly journey: CaseJourney;
  readonly actions?: Readonly<Record<string, React.ReactNode>> | undefined;
  readonly open: boolean;
  readonly onToggle: () => void;
}): React.JSX.Element {
  return (
    <div className="rh-v4-meter__journey">
      <V4Why
        label={t('webAdmin.caseDetailV4.journeyShow', { ...cs, values: { total: journey.total } })}
        hideLabel={t('webAdmin.caseDetailV4.journeyHide', cs)}
        open={open}
        onToggle={onToggle}
        testId="case-progress-journey"
      >
        <p className="rh-a2-meta" style={{ margin: 0 }}>
          {journey.cancelled
            ? t('webAdmin.caseCockpit.remaining.cancelled', cs)
            : t('webAdmin.caseCockpit.remaining.hint', cs)}
        </p>
        <ul className="rh-a2-rows" data-testid="case-cockpit-remaining-list">
          {journey.steps.map((step) => (
            <li
              key={step.stage}
              className={step.state === 'done' ? 'rh-a2-rows__done' : undefined}
              data-testid={`case-journey-${step.stage}`}
              data-state={step.state}
            >
              <span
                className={`rh-a2-dot ${step.state === 'done' ? 'rh-a2-dot--ok' : step.state === 'now' ? 'rh-a2-dot--accent' : ''}`}
                aria-hidden="true"
              />
              <span className="rh-a2-grow">
                <span className="rh-a2-title">{step.label}</span>
                {step.enteredAt !== null ? (
                  <span className="rh-a2-meta">
                    {t('webAdmin.caseCockpit.remaining.enteredOn', { ...cs, values: { date: czDate(step.enteredAt) } })}
                  </span>
                ) : null}
              </span>
              {step.state === 'now' ? (
                <span className="rh-a2-badge rh-a2-badge--accent">{t('webAdmin.caseCockpit.remaining.badgeNow', cs)}</span>
              ) : null}
              {/* Krok NOC3 bod 1 (nalez N18): faze s akci je KLIKACI. */}
              {actions?.[step.stage] ?? null}
            </li>
          ))}
        </ul>
      </V4Why>
    </div>
  );
}

/**
 * Obsah sekce „Prověření" (predloha): nahore nejvys dva PROBLEMY, kazdy s
 * rozbalenim „Proč a co s tím" (vysvetleni · co udelat · zdroj), pak body
 * proverení se stavem a jednou akci, dole odkaz „Zdroje a celý report".
 */
export function CaseReviewSection({
  status,
  review,
  onAreaAction,
  onOpenReport,
  onOpenInAudit,
  formatDate,
  freshness,
  children,
}: {
  readonly status: CockpitStatus;
  readonly review: CaseReviewAreas;
  readonly onAreaAction: (area: ReviewArea) => void;
  readonly onOpenReport: () => void;
  /** „Otevřít v auditu" u problemu — zustava z UXR5, jen uvnitr rozbaleni. */
  readonly onOpenInAudit: (tab: CockpitIssue['tab'], anchor: string | null) => void;
  readonly formatDate: (iso: string) => string;
  /** Stitek aktualnosti s „Prověřit znovu" (ROZHODNUTI UXR5-3 — zustava). */
  readonly freshness?: React.ReactNode;
  /** Co stoji pod oblastmi (ukoly makléře). */
  readonly children?: React.ReactNode;
}): React.JSX.Element {
  const [allIssues, setAllIssues] = React.useState(false);
  const [openWhy, setOpenWhy] = React.useState<ReadonlySet<string>>(new Set());
  const shown = allIssues ? status.issues : status.issues.slice(0, VISIBLE_ISSUES);
  const hidden = status.issues.length - shown.length;

  return (
    <div className="rh-v4-review" id="case-review" data-testid="case-review">
      {freshness}
      {status.issues.length === 0 ? (
        <p className="rh-a2-meta" style={{ margin: 0 }} data-testid="case-cockpit-status-empty">
          {t('webAdmin.caseCockpit.status.empty', cs)}
        </p>
      ) : null}
      <div data-testid="case-cockpit-status" data-light={status.light}>
        <span hidden data-testid="case-cockpit-light" data-light={status.light} />
        {status.issues.length === 0 ? null : (
          <div data-testid="case-cockpit-issues">
            {shown.map((issue) => (
              <IssueRow
                key={issue.id}
                issue={issue}
                open={openWhy.has(issue.id)}
                onToggle={() => {
                  setOpenWhy((current) => {
                    const next = new Set(current);
                    if (next.has(issue.id)) next.delete(issue.id);
                    else next.add(issue.id);
                    return next;
                  });
                }}
                formatDate={formatDate}
                onOpenInAudit={onOpenInAudit}
              />
            ))}
          </div>
        )}
      </div>
      {hidden > 0 ? (
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--plain"
          onClick={() => { setAllIssues(true); }}
          data-testid="case-issues-more"
        >
          {t('webAdmin.caseDetailV4.moreIssues', { ...cs, values: { count: hidden } })}
        </button>
      ) : null}

      <ul className="rh-v4-areas" data-testid="case-review-areas">
        {review.areas.map((area) => (
          <li key={area.claimType} data-testid={area.testId} data-verified={String(area.verified)}>
            <V4State state={area.verified ? 'done' : 'later'} />
            <span className="rh-v4-areas__name">{area.label}</span>
            <button
              type="button"
              className="rh-v4-btn rh-v4-btn--plain"
              onClick={() => { onAreaAction(area); }}
              data-testid={`${area.testId}-action`}
            >
              {AREA_ACTION_LABEL[area.action.kind]}
            </button>
          </li>
        ))}
      </ul>
      {children}
      <button
        type="button"
        className="rh-v4-btn rh-v4-btn--plain"
        onClick={onOpenReport}
        data-testid="case-sources-and-report"
      >
        {t('webAdmin.caseDetailV4.sourcesAndReport', cs)}
      </button>
    </div>
  );
}

/**
 * Popisky akci oblasti — TENTYZ katalog, jaky pouziva karta „Prověření"
 * (`case-overview.tsx`). Dve kopie by se rozesly prvni zmenou slova.
 */
const AREA_ACTION_LABEL: Readonly<Record<ReviewArea['action']['kind'], string>> = {
  view: t('webAdmin.caseReview.viewAction', cs),
  lookup: t('webAdmin.caseReview.lookupAction', cs),
  answer: t('webAdmin.caseReview.enterAction', cs),
  open: t('webAdmin.caseReview.enterAction', cs),
};

function IssueRow({
  issue,
  open,
  onToggle,
  formatDate,
  onOpenInAudit,
}: {
  readonly issue: CockpitIssue;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly formatDate: (iso: string) => string;
  readonly onOpenInAudit: (tab: CockpitIssue['tab'], anchor: string | null) => void;
}): React.JSX.Element {
  return (
    <div className="rh-v4-issue" data-testid={issue.testId}>
      <div className="rh-v4-issue__head">
        <V4State state={issue.severity === 'blocker' ? 'wait' : 'later'} />
        <span className="rh-v4-issue__text">{issue.sentence}</span>
      </div>
      <V4Why
        label={t('webAdmin.caseDetailV4.why', cs)}
        hideLabel={t('webAdmin.caseDetailV4.whyHide', cs)}
        open={open}
        onToggle={onToggle}
        testId={`${issue.testId}-why`}
      >
        <div>{issue.explanation}</div>
        <div>{issue.todo}</div>
        {issue.source !== null && issue.at !== null ? (
          <div className="rh-v4-why__source">
            {t('webAdmin.caseCockpit.status.sourceLine', {
              ...cs,
              values: { source: issue.source, date: formatDate(issue.at) },
            })}
          </div>
        ) : null}
        {/* „Otevřít v auditu" z UXR5 — funkce zustava, jen uvnitr rozbaleni. */}
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--plain"
          onClick={() => { onOpenInAudit(issue.tab, issue.anchor); }}
          data-testid={`${issue.testId}-open`}
        >
          {t('webAdmin.caseCockpit.status.openInAudit', cs)}
        </button>
      </V4Why>
    </div>
  );
}
